/**
 * ChatChannel 类型和 Schema 定义
 *
 * ⚠️ 注意: 此文件由脚本自动生成，请勿手动修改
 * 如需更新，请运行代码生成脚本
 */
import type { SchemaDefinition } from './_schema'

/**
 * ChatChannel
 */
export interface ChatChannel {
  /** 创建时间（UTC） */
  created_at?: string
  /** 更新时间（UTC） */
  updated_at?: string
  /** 删除时间（UTC），goframe会自动处理deleted_at，如果想避免goframe自动处理，请使用Unscoped() */
  deleted_at?: string
  /** 删除原因 */
  deleted_reason?: string
  /** [S] 频道ID（不含类型前缀） */
  id?: string
  /** [S] 频道类型 */
  type?: string
  /** [S] 频道CID，包含类型前缀，格式为{channel_type}:{channel_id} */
  cid?: string
  /** [S] 创建者用户ID */
  creator_id?: string
  /** [S] 成员数量 */
  member_count?: number
  /** [S] 是否冻结（禁止发消息） */
  frozen?: boolean
  /** [S] 是否禁用 */
  disabled?: boolean
  /** [S] 最后消息时间 */
  last_message_at?: string
  /** [S] 最后消息文本 */
  last_message_text?: string
  /** [S] 清空消息时间 */
  truncated_at?: string
  /** [S] 扩展数据 */
  extra_data?: Record<string, any>
  /** [L] 当前群主用户ID（可变，用于权限判断） */
  owner_id?: string
  /** [L] 关联的群组ID（Camp/Battle） */
  group_id?: string
  /** [L] 成员用户ID列表（私聊时存双方ID） */
  member_user_ids?: string[]
  /** [L] Stream 同步状态 */
  stream_sync_status?: string
  /** [L] 同步错误 */
  stream_sync_error?: string
  /** [L] 同步时间 */
  stream_synced_at?: string
}

/**
 * ChatChannel Schema
 */
export const ChatChannelSchema: SchemaDefinition = {
  properties: {
    created_at: {
      description: '创建时间（UTC）',
      format: '*time.Time',
      type: 'string',
      label: '创建时间',
    },
    updated_at: {
      description: '更新时间（UTC）',
      format: '*time.Time',
      type: 'string',
      label: '更新时间',
    },
    deleted_at: {
      description:
        '删除时间（UTC），goframe会自动处理deleted_at，如果想避免goframe自动处理，请使用Unscoped()',
      format: '*time.Time',
      type: 'string',
      label: '删除时间',
    },
    deleted_reason: {
      description: '删除原因',
      format: 'string',
      type: 'string',
      label: '删除原因',
    },
    id: {
      description: '[S] 频道ID（不含类型前缀）',
      format: 'string',
      type: 'string',
    },
    type: {
      description: '[S] 频道类型',
      format: 'cons.ChatChannelType',
      type: 'string',
    },
    cid: {
      description: '[S] 频道CID，包含类型前缀，格式为{channel_type}:{channel_id}',
      format: 'string',
      type: 'string',
    },
    creator_id: {
      description: '[S] 创建者用户ID',
      format: 'string',
      type: 'string',
    },
    member_count: {
      description: '[S] 成员数量',
      format: 'int',
      type: 'integer',
    },
    frozen: {
      description: '[S] 是否冻结（禁止发消息）',
      format: 'bool',
      type: 'boolean',
    },
    disabled: {
      description: '[S] 是否禁用',
      format: 'bool',
      type: 'boolean',
    },
    last_message_at: {
      description: '[S] 最后消息时间',
      format: '*time.Time',
      type: 'string',
    },
    last_message_text: {
      description: '[S] 最后消息文本',
      format: 'string',
      type: 'string',
    },
    truncated_at: {
      description: '[S] 清空消息时间',
      format: '*time.Time',
      type: 'string',
    },
    extra_data: {
      additionalProperties: { type: 'any', description: '' },
      description: '[S] 扩展数据',
      format: 'map[string]interface {}',
      type: 'object',
    },
    owner_id: {
      description: '[L] 当前群主用户ID（可变，用于权限判断）',
      format: 'string',
      type: 'string',
    },
    group_id: {
      description: '[L] 关联的群组ID（Camp/Battle）',
      format: 'string',
      type: 'string',
    },
    member_user_ids: {
      description: '[L] 成员用户ID列表（私聊时存双方ID）',
      format: '[]string',
      items: {
        format: 'string',
        type: 'string',
      },
      type: 'array',
    },
    stream_sync_status: {
      description: '[L] Stream 同步状态',
      format: 'cons.StreamSyncStatus',
      type: 'string',
    },
    stream_sync_error: {
      description: '[L] 同步错误',
      format: 'string',
      type: 'string',
    },
    stream_synced_at: {
      description: '[L] 同步时间',
      format: '*time.Time',
      type: 'string',
    },
  },
  type: 'object',
}
